import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  makeStyles,
  createStyles,
} from '@bb-ui/react-library';
import { TenantData } from 'App.types';
import { useTenantIndex } from 'hooks/useTenantIndex';
import { useLinkedIlluminates } from 'hooks/useLinkedIlluminates';
import { getComparedRegion, LearnDataExport, useLearnDataExport } from 'hooks/useLearnDataExport';
import { BooleanStatusIcon, DataExportStatus } from 'components/DataSharing/DataExportStatus';
import { useDataSharingTableStyles } from './DataSharingSection';

const useStyles = makeStyles((theme) =>
  createStyles({
    dataExportRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    dataExportLabel: {
      fontWeight: theme.typography.fontWeightBold,
    },
  }),
);

interface LinkedTenantDataExportProps {
  linkedTenant?: TenantData;
  learnDataExport?: LearnDataExport;
  error?: boolean;
}

/** Data export status of the tenant whose Illuminate links are shown */
const LinkedTenantDataExport: React.FC<LinkedTenantDataExportProps> = ({
  linkedTenant,
  learnDataExport,
  error,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  let status: React.ReactNode = t('dataSharing.dataExportLoading');
  if (error) {
    status = t('dataSharing.dataExportUnknown');
  } else if (linkedTenant) {
    status = (
      <DataExportStatus
        tenantType={linkedTenant.tenantType ?? null}
        learnDataExport={learnDataExport}
      />
    );
  }

  return (
    <div className={classes.dataExportRow} data-testid="linked-tenant-data-export">
      <Typography variant="body2" className={classes.dataExportLabel}>
        {t('dataSharing.dataExport')}
      </Typography>
      {status}
    </div>
  );
};

export interface LinkedIlluminateSectionProps {
  /** The Learn or Ally tenant that shares its data with Illuminate tenants */
  tenantId: string;
  /** Shows the data export status of the tenant above the table */
  showDataExport?: boolean;
}

/** Shows the Illuminate tenants this tenant is linked to for data sharing */
export const LinkedIlluminateSection: React.FC<LinkedIlluminateSectionProps> = ({
  tenantId,
  showDataExport = true,
}) => {
  const { t } = useTranslation();
  const classes = useDataSharingTableStyles();

  const { index, error: indexError } = useTenantIndex();
  const linkedTenant = index?.getTenant(tenantId);
  // The tenant list failed to load, or the tenant is not in it
  const tenantError = indexError || (index && !linkedTenant);
  const learnTenants = React.useMemo(
    () => (linkedTenant ? [linkedTenant] : undefined),
    [linkedTenant],
  );
  const { dataExport, resourceRegions } = useLearnDataExport(learnTenants);

  // For Learn tenants this is the region of their resources, see useLearnDataExport.
  // Links are stored in the region of the Illuminate tenant, which should be this same region.
  // A link to an Illuminate in another region is not found here, so no region warning is shown.
  const comparedRegion = linkedTenant
    ? getComparedRegion(linkedTenant, resourceRegions)
    : undefined;
  const linked = useLinkedIlluminates(
    comparedRegion ? tenantId : undefined,
    comparedRegion?.region,
  );
  const loading = !tenantError && (!comparedRegion || linked.loading);
  const error = tenantError || linked.error;
  const { illuminates } = linked;

  let content = (
    <CircularProgress
      ariaLabel={t('global.loadingIndicator.loading').toString()}
      data-testid="loading-linked-illuminates"
    />
  );

  if (error) {
    content = (
      <Typography data-testid="linked-illuminates-error">{t('global.fetchError')}</Typography>
    );
  } else if (!loading) {
    if (illuminates.length === 0) {
      content = <Typography>{t('dataSharing.noLinkedIlluminates')}</Typography>;
    } else {
      content = (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={3} className={classes.groupHeader}>
                {t('dataSharing.illuminateDetails')}
              </TableCell>
              <TableCell
                colSpan={2}
                className={`${classes.groupHeader} ${classes.sharingGroupHeader}`}
              >
                {t('dataSharing.sharing')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.validationHeader}>
                {t('dataSharing.tenantName')}
              </TableCell>
              <TableCell className={classes.validationHeader}>
                {t('dataSharing.tenantId')}
              </TableCell>
              <TableCell className={classes.validationHeader}>{t('dataSharing.region')}</TableCell>
              <TableCell className={`${classes.sharingHeader} ${classes.sharingFirstColumn}`}>
                {t('dataSharing.dataSharing')}
              </TableCell>
              <TableCell className={classes.sharingHeader}>{t('dataSharing.reporting')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {illuminates.map((illuminate) => (
              <TableRow key={illuminate.id}>
                <TableCell className={classes.tableCell}>
                  <Link
                    href={`/tenants/${illuminate.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.tenantLink}
                  >
                    {illuminate.name || t('tenantInformation.unset')}
                  </Link>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Link
                    href={`/tenants/${illuminate.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.tenantLink}
                  >
                    {illuminate.id}
                  </Link>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {illuminate.region || t('tenantInformation.unset')}
                </TableCell>
                <TableCell className={`${classes.sharingCell} ${classes.sharingFirstColumn}`}>
                  {/* Every listed Illuminate tenant is linked, so data sharing is always on */}
                  <BooleanStatusIcon size="medium" value label={t('global.yes')} />
                </TableCell>
                <TableCell className={classes.sharingCell}>
                  {illuminate.usedInReporting === null ? (
                    t('dataSharing.dataExportUnknown')
                  ) : (
                    <BooleanStatusIcon
                      size="medium"
                      value={illuminate.usedInReporting}
                      label={illuminate.usedInReporting ? t('global.yes') : t('global.no')}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
  }

  return (
    <div className={classes.section}>
      <Typography className={classes.heading} component="h2" variant="h4">
        {t('dataSharing.linkedIlluminatesTitle')}
      </Typography>
      {showDataExport && (
        <LinkedTenantDataExport
          linkedTenant={linkedTenant}
          learnDataExport={linkedTenant ? dataExport[linkedTenant.id] : undefined}
          error={!!tenantError}
        />
      )}
      {content}
    </div>
  );
};
